import React from 'react'
import { useHistory } from 'react-router'
import { Routes } from '@SonarCX/Routes'
import { useAppDispatch, useAppSelector } from '@SonarCX/hooks/redux'
import { configure } from '@SonarCX/lib/mixpanel'

import { fetchFavorites } from '@SonarCX/slices/favorites'
import { updateFromEnv as updateAccess } from '@SonarCX/slices/accessSlice'
import {
  selectCustomFields,
  fetchCustomFields
} from '@SonarCX/slices/customFields'
import { getStoredAccount, useAccounts } from '../AccountProvider'
import { useAuth } from '../AuthProvider'
import { useAgent } from '../AgentProvider'

const EnvironmentContext = React.createContext(null)

const EnvironmentProvider = ({ children }) => {
  const dispatch = useAppDispatch()
  const history = useHistory()
  const { loaded: loadedFields, loading } = useAppSelector(selectCustomFields)

  const { isLoggedIn } = useAuth()
  const { callEnvironment } = useAccounts()
  const { setUser } = useAgent()

  const [loaded, setLoaded] = React.useState(false)
  const [user, setEnvUser] = React.useState(null)
  const [env, setEnv] = React.useState(null)
  const [error, setError] = React.useState(null)
  const afterLoadRef = React.useRef(null)

  React.useEffect(() => {
    if (!isLoggedIn || loaded) {
      return
    }

    const account = getStoredAccount()
    if (!account) {
      //redirect to account select
      history.push(Routes.LOGIN)
      return
    }

    setEnvUser(null)
    setEnv(null)
    setError(null)
    callEnvironment(account, false, data => {
      setEnv(data)
      dispatch(updateAccess(data))
      setUser(data.user)
      setEnvUser(data.user)
      configure(data)
      setLoaded(true)
      if (afterLoadRef.current) {
        afterLoadRef.current()
        afterLoadRef.current = null
      }
    }).catch(err => {
      console.error('Error loading environment', err)
      setError(err)
      setLoaded(true)
    })
  }, [isLoggedIn, loaded, callEnvironment, setUser, history, dispatch])

  // Load custom fields
  React.useEffect(() => {
    if (!isLoggedIn) {
      return
    }

    if (!loadedFields && !loading) {
      dispatch(fetchCustomFields())
    }
  }, [isLoggedIn, loadedFields, loading, dispatch])

  // Load favorites
  React.useEffect(() => {
    if (!isLoggedIn) {
      return
    }

    if (!user) {
      return
    }

    dispatch(fetchFavorites(user.id))
  }, [isLoggedIn, user, dispatch])

  const update = React.useCallback(
    newUser => {
      const updated = user ? { ...user, ...newUser } : newUser
      setEnvUser(updated)
    },
    [user]
  )

  const getEnvValue = React.useCallback(
    (path, key, fallback = '') => {
      try {
        if (!env) {
          return fallback
        }

        const steps = path.split('.')
        let arr = env
        steps.forEach(s => {
          arr = arr[s]
        })

        return arr.find(item => item.key === key)?.value ?? fallback
      } catch {
        return fallback
      }
    },
    [env]
  )

  const reload = React.useCallback(() => {
    setLoaded(false)
  }, [])

  return (
    <EnvironmentContext.Provider
      value={{
        user,
        setEnvUser,
        loaded,
        update,
        env,
        getEnvValue,
        reload,
        error,
        afterLoadRef
      }}
    >
      {children}
    </EnvironmentContext.Provider>
  )
}

export default EnvironmentProvider

export const useEnvironment = () => React.useContext(EnvironmentContext)
